import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import { useAppConfig } from "../../../AppConfigProvider";
import { useFocusEffect, useRoute, RouteProp } from "@react-navigation/native";
import {
  AttachmentType,
  StoragePathType,
  useProcessAttachmentMutation,
  useGetFileQuery,
  useGetLearningTtsTokenMutation,
} from "../../../graphql/generated/graphql";
import AITutorChatBar from "../../../components/learning-activity/AITutorChatBar/AITutorChatBar";
import { usePostHog } from "posthog-js/react";
import useLearningLlmStream, { type StreamAudioChunkPayload } from "../../../hooks/useLearningLlmStream";
import { useLearningTtsWebSocket } from "../../../hooks/useLearningTtsWebSocket";
import useAITutorAudioInput from "../../../hooks/useAITutorAudioInput";
import Sentry from "../../../utils/sentry";
import useFileUpload, { ProcessedFile } from "../../../hooks/useFileUpload";
import type { RootStackParamList } from "../../../navigation/navigation.types";

const viewsWithoutAttachments = ["Onboarding", "Custom Resource Student"];

export interface AITutorChatBarAttachment {
  id: string;
  title: string;
}

interface AITutorChatBarContainerProps {
  onSubmit?: () => void;
  chatId?: string | null;
  contentNodeId: string;
  onChatBarHeightChange?: (height: number) => void;
  onStreamComplete?: (chatId?: string) => void;
  onStreamError?: (payload: { title: string; message: string; onRetry?: () => void }) => void;
  onStreamChunk?: (payload: { text: string; isFinalChunk?: boolean }) => void;
  onAudioStreamChunk?: (payload: StreamAudioChunkPayload) => void;
  onAudioToggleChange?: (enabled: boolean) => void;
  onObjectiveCompleted?: (objectiveIds: string[]) => void;
  onAllObjectivesCompleted?: () => void;
  onStreamStart?: (
    message: string,
    attachment?: AITutorChatBarAttachment | null,
    meta?: { isAutoStart?: boolean }
  ) => void;
  onAudioStreamStarted?: () => void;
  onAudioStreamEnded?: () => void;
  initialMessage?: string;
  autoStart?: boolean;
}

function AITutorChatBarContainer({
  onSubmit = () => {},
  chatId,
  contentNodeId,
  onChatBarHeightChange,
  onStreamComplete,
  onStreamError,
  onStreamChunk,
  onAudioStreamChunk,
  onAudioToggleChange,
  onObjectiveCompleted,
  onAllObjectivesCompleted,
  onStreamStart,
  onAudioStreamStarted,
  onAudioStreamEnded,
  initialMessage,
  autoStart,
}: AITutorChatBarContainerProps) {
  const { companionName } = useAppConfig();
  const posthog = usePostHog();

  const { state, dispatch } = useAppState();
  const { startStream, isStreaming } = useLearningLlmStream();
  const [getLearningTtsToken] = useGetLearningTtsTokenMutation();

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      const result = await getLearningTtsToken();
      return result.data?.getLearningTtsToken?.token ?? null;
    } catch {
      return null;
    }
  }, [getLearningTtsToken]);

  const ttsWs = useLearningTtsWebSocket({
    onAudioChunk: (payload) => {
      onAudioStreamChunk?.(payload);
    },
    getToken,
    onStreamStarted: onAudioStreamStarted,
    onStreamEnded: onAudioStreamEnded,
  });

  const route = useRoute<RouteProp<RootStackParamList, keyof RootStackParamList>>();

  const {
    data: fileData,
    startPolling,
    stopPolling,
    refetch,
  } = useGetFileQuery({ variables: { fileId: "" }, fetchPolicy: "network-only" });

  const chatInputRef = useRef(null);

  const [inputValue, setInputValue] = useState("");
  const [isAudioOn, setIsAudioOn] = useState(state.meta.isAudioOn);
  const [attachment, setAttachment] = useState(null);
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);
  const [activeChatId, setActiveChatId] = useState<string | null>(chatId ?? null);

  const [processAttachment] = useProcessAttachmentMutation();

  const isAttachmentEnabled = !viewsWithoutAttachments.includes(route.name);
  const hasAutoStartedRef = useRef(false);

  useEffect(() => {
    setActiveChatId(chatId ?? null);
  }, [chatId]);

  useFocusEffect(
    useCallback(() => {
      // Function to execute when the screen is focused
      const handleDocumentKeyPress = () => {
        chatInputRef.current?.focus();
      };

      if (state.meta.keypressIsListening) {
        // Add the event listener only if input is not focused and the modal is not open
        document.addEventListener("keypress", handleDocumentKeyPress);
      } else {
        // Remove the event listener if input is focused or the modal is open
        document.removeEventListener("keypress", handleDocumentKeyPress);
      }

      // Cleanup the event listener when the component is unmounted
      return () => {
        document.removeEventListener("keypress", handleDocumentKeyPress);
      };
    }, [state.meta.keypressIsListening])
  );

  useEffect(() => {
    if (fileData?.getFile?.data?.length > 0) {
      const file = fileData.getFile.data[0];
      const isLoading = file.status !== "completed";
      if (!isLoading) {
        setIsAttachmentLoading(false);
        stopPolling();
      }
    }
  }, [fileData, setIsAttachmentLoading, stopPolling]);

  useEffect(() => {
    if (!autoStart || hasAutoStartedRef.current) {
      return;
    }
    if (!initialMessage?.trim()) {
      return;
    }
    hasAutoStartedRef.current = true;
    void sendMessageToAxio(initialMessage, null, { isAutoStart: true });
  }, [autoStart, initialMessage]);

  const handleSubmit = () => {
    handleSubmitEditing(undefined);
    onSubmit();
  };

  const handleChangeText = (text) => {
    setInputValue(text);
  };

  const handleBeforeUpload = () => {
    setIsAttachmentLoading(true);
  };

  const handleAfterUpload = async (processedFilesArray: ProcessedFile[]) => {
    for (const processedFile of processedFilesArray) {
      await processAttachment({
        variables: {
          fileId: processedFile.file.id,
        },
      });
      // Add the new file to the files array
      setAttachment({
        title: processedFile.file.fileName,
        fileType: processedFile.mimeType,
        id: processedFile.file.id,
        status: "pending",
      });
      refetch({ fileId: processedFile.file.id });
    }

    startPolling(3000);
  };

  const handleFileUploadError = () => {
    setIsAttachmentLoading(false);
    setAttachment(null);
  };

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.ChatAttachments,
    beforeUpload: handleBeforeUpload,
    afterUpload: handleAfterUpload,
    onError: handleFileUploadError,
    multiple: true,
  });

  const handleDeleteAttachment = () => {
    setAttachment(null);
  };

  const handleSubmitEditing = async (quickItemValue) => {
    try {
      if (isAttachmentLoading === true || isStreaming) return;

      posthog?.capture("Message sent to axio");
      setAttachment(null);

      if (state.meta.isAdventure) {
        dispatch({
          type: actions.SET_META,
          payload: { isAdventure: false },
        });
      }

      setInputValue("");
      await sendMessageToAxio(quickItemValue);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const sendMessageToAxio = async (
    quickItemValue?: string,
    attachmentOverride?: { id: string } | null,
    options?: { isAutoStart?: boolean }
  ) => {
    if (isStreaming) return;
    const message = (quickItemValue || inputValue || "").trim();
    if (!message) return;

    const activeAttachment = attachmentOverride ?? attachment;

    onStreamStart?.(message, activeAttachment ? { id: activeAttachment.id, title: activeAttachment.title } : null, {
      isAutoStart: options?.isAutoStart,
    });

    const attachmentInput = activeAttachment
      ? {
          type: AttachmentType.Document,
          id: activeAttachment.id,
          fileId: activeAttachment.id,
        }
      : undefined;

    if (isAudioOn) {
      ttsWs.connect();
    }

    await startStream(message, {
      chatId: activeChatId ?? undefined,
      contentNodeId,
      attachment: attachmentInput,
      enableTts: isAudioOn,
      onChunk: ({ text, isFinalChunk }) => {
        onStreamChunk?.({ text, isFinalChunk });
      },
      onAudioSegmentStart: () => {
        ttsWs.notifySegmentStart();
      },
      onAudioSegmentText: ({ chunkIndex, text }) => {
        ttsWs.sendSegmentText(chunkIndex, text);
      },
      onAudioSegmentEnd: () => {
        ttsWs.notifySegmentEnd();
      },
      onChatId: (chatId) => {
        setActiveChatId(chatId);
      },
      onObjectiveCompleted: ({ objectiveIds }) => {
        onObjectiveCompleted?.(objectiveIds);
      },
      onAllObjectivesCompleted: () => {
        onAllObjectivesCompleted?.();
      },
      onComplete: ({ event }) => {
        if (event?.chatId) {
          setActiveChatId(event.chatId);
        }
        onStreamComplete?.(event?.chatId);
      },
      onError: ({ error }) => {
        ttsWs.disconnect();
        Sentry.captureException(error);
        onStreamError?.({
          title: "Failed to send message",
          message: error.message || "An error occurred while sending your message. Please try again.",
          onRetry: async () => {
            await sendMessageToAxio(message, activeAttachment, options);
          },
        });
      },
    });
  };

  const { isRecording, isTranscribing, handleRecordingStopPress, handleRecordingStartPress } = useAITutorAudioInput({
    isStreaming,
    isAttachmentLoading,
    onSubmit,
    sendMessageToAxio: async (message: string) => {
      await sendMessageToAxio(message);
    },
    onStreamError,
  });

  const onAudioOffPress = () => {
    setIsAudioOn(false);
    onAudioToggleChange?.(false);
    ttsWs.disconnect();
  };

  const onAudioOnPress = () => {
    setIsAudioOn(true);
    onAudioToggleChange?.(true);
  };

  const placeholderText = isRecording
    ? `${companionName} is listening...`
    : isTranscribing
      ? "Processing your recording..."
      : "Type your response or ask a question...";

  return (
    <>
      <AITutorChatBar
        value={inputValue}
        isRecording={isRecording}
        isAudioOn={isAudioOn}
        placeholder={placeholderText}
        inputRef={chatInputRef}
        onChangeText={handleChangeText}
        onSubmitEditing={handleSubmit}
        isDisabled={isStreaming || isTranscribing}
        onAudioOffPress={onAudioOffPress}
        onAudioOnPress={onAudioOnPress}
        onRecordingStopPress={handleRecordingStopPress}
        onRecordingStartPress={handleRecordingStartPress}
        isAttachmentEnabled={isAttachmentEnabled}
        attachment={attachment}
        isAttachmentLoading={isAttachmentLoading}
        onAttachmentPress={handleFileUpload}
        onDeleteAttachmentPress={handleDeleteAttachment}
        onHeightChange={onChatBarHeightChange}
      />
    </>
  );
}

export default AITutorChatBarContainer;
